/**
 * Prefetch tiles mappa per uso offline (delega al Service Worker + Cache API `map-tiles`).
 */
import { postPrefetchTilesMessage } from './sw-messages';

const DEFAULT_MIN_LAT = 38.5;
const DEFAULT_MIN_LON = 68.5;
const DEFAULT_MAX_LAT = 41.0;
const DEFAULT_MAX_LON = 71.5;
const DEFAULT_MIN_ZOOM = 8;
const DEFAULT_MAX_ZOOM = 14;

function envNumber(name, fallback) {
  const v = process.env[name];
  if (v === undefined || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Restituisce bbox nel formato atteso dal service worker: [minLon, minLat, maxLon, maxLat].
 * @returns {[number, number, number, number]}
 */
export function getOfflineMapBboxFromEnv() {
  return [
  envNumber('REACT_APP_OFFLINE_MIN_LON', DEFAULT_MIN_LON),
  envNumber('REACT_APP_OFFLINE_MIN_LAT', DEFAULT_MIN_LAT),
  envNumber('REACT_APP_OFFLINE_MAX_LON', DEFAULT_MAX_LON),
  envNumber('REACT_APP_OFFLINE_MAX_LAT', DEFAULT_MAX_LAT)];

}

/**
 * @param {[number, number, number, number]} bbox minLon, minLat, maxLon, maxLat
 * @param {number} minZoom
 * @param {number} maxZoom
 * @param {(downloaded: number, total: number) => void} [onProgress]
 * @returns {Promise<{ downloaded: number, total: number, failed: number }>}
 */
export async function prefetchMapTiles(bbox, minZoom, maxZoom, onProgress) {
  const mz = Number.isFinite(minZoom) ? minZoom : DEFAULT_MIN_ZOOM;
  const Mz = Number.isFinite(maxZoom) ? maxZoom : DEFAULT_MAX_ZOOM;
  const est = estimateTilesSize(bbox, mz, Mz);




  return postPrefetchTilesMessage(bbox, [mz, Mz], onProgress);
}

/**
 * Prefetch usando solo variabili d'ambiente REACT_APP_OFFLINE_* e zoom.
 * @param {(downloaded: number, total: number) => void} [onProgress]
 */
export async function prefetchMapTilesFromEnv(onProgress) {
  const bbox = getOfflineMapBboxFromEnv();
  const minZ = envNumber('REACT_APP_OFFLINE_MIN_ZOOM', DEFAULT_MIN_ZOOM);
  const maxZ = envNumber('REACT_APP_OFFLINE_MAX_ZOOM', DEFAULT_MAX_ZOOM);

  return prefetchMapTiles(bbox, minZ, maxZ, onProgress);
}

/**
 * Stima approssimativa del numero di tile e della dimensione.
 * @param {[number, number, number, number]} bbox
 * @param {number} minZoom
 * @param {number} maxZoom
 */
export function estimateTilesSize(bbox, minZoom, maxZoom) {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  let count = 0;
  for (let z = minZoom; z <= maxZoom; z++) {
    const nw = latLonToTile(maxLat, minLon, z);
    const se = latLonToTile(minLat, maxLon, z);
    const minX = Math.min(nw.x, se.x);
    const maxX = Math.max(nw.x, se.x);
    const minY = Math.min(nw.y, se.y);
    const maxY = Math.max(nw.y, se.y);
    count += (maxX - minX + 1) * (maxY - minY + 1);
  }
  const estimatedBytes = count * 20 * 1024;
  return {
    tileCount: count,
    estimatedMB: (estimatedBytes / (1024 * 1024)).toFixed(1)
  };
}

/**
 * @param {number} lat
 * @param {number} lon
 * @param {number} zoom
 */
function latLonToTile(lat, lon, zoom) {
  const n = Math.pow(2, zoom);
  const x = Math.floor((lon + 180) / 360 * n);
  const latRad = lat * Math.PI / 180;
  const y = Math.floor(
    (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n
  );
  return { x, y };
}